import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CloverApiService } from './cloverApi.service';
import { UtilsService } from './utils.service';

export interface StandbyConfig {
  enabled: boolean;
  idleTimeoutSeconds: number;
  slideDurationSeconds: number;
}

/** A single screensaver slide: full-bleed image plus optional caption. */
export interface StandbySlide {
  image: string;
  title?: string;
  subtitle?: string;
}

const DEFAULTS: StandbyConfig = {
  enabled: false,
  idleTimeoutSeconds: 600,
  slideDurationSeconds: 7,
};

// Interaction events that count as "the customer is here" — a hit on any of
// them dismisses the screensaver / resets the idle clock. Captured passively
// so we never interfere with the app's own handlers.
const ACTIVITY_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'wheel'];

/**
 * Drives the idle standby / screensaver for customer-facing devices (kiosk,
 * table, confirmation display).
 *
 * Safety by design:
 *  - start() is a no-op unless the merchant enabled kioskStandby, so existing
 *    devices behave exactly as before.
 *  - engage() refuses to show a blank screen: with zero images we simply never
 *    activate.
 *  - Every image source is best-effort; a failing API call falls through to
 *    the next source instead of wedging.
 */
@Injectable({ providedIn: 'root' })
export class StandbyService {
  private activeSubject = new BehaviorSubject<boolean>(false);
  /** Emits true while the screensaver should be shown. */
  readonly active$: Observable<boolean> = this.activeSubject.asObservable();

  /** Slides to cycle through, in merchant-priority order. */
  slides: StandbySlide[] = [];
  slideDurationSeconds = DEFAULTS.slideDurationSeconds;

  private idleTimeoutMs = DEFAULTS.idleTimeoutSeconds * 1000;
  private idleTimer: any = null;
  private running = false;
  private readonly boundActivity: () => void;

  constructor(
    private cloverApi: CloverApiService,
    private utils: UtilsService,
    private zone: NgZone,
  ) {
    this.boundActivity = () => this.notifyActivity();
  }

  get isActive(): boolean {
    return this.activeSubject.value;
  }

  /**
   * Begin watching for inactivity. No-op (and stays disengaged forever) when
   * the merchant hasn't enabled standby. Idempotent — a second call while
   * already running does nothing.
   */
  start(): void {
    if (this.running) { return; }
    const cfg = this.readConfig();
    if (!cfg.enabled) { return; }

    this.idleTimeoutMs = cfg.idleTimeoutSeconds * 1000;
    this.slideDurationSeconds = cfg.slideDurationSeconds;
    this.running = true;

    this.loadSlides();
    this.addListeners();
    this.armTimer();
  }

  /** Tear everything down. Safe to call when not running. */
  stop(): void {
    this.running = false;
    this.clearTimer();
    this.removeListeners();
    if (this.activeSubject.value) { this.activeSubject.next(false); }
  }

  /**
   * Any user interaction. Dismiss the screensaver if it's showing, otherwise
   * just restart the idle clock. Public so a host can also forward events its
   * own handlers swallow (e.g. a full-screen overlay tap).
   */
  notifyActivity(): void {
    if (!this.running) { return; }
    if (this.activeSubject.value) {
      this.activeSubject.next(false);
    }
    this.armTimer();
  }

  private readConfig(): StandbyConfig {
    const cfg = this.utils.getCurrentUser()?.orgSettings?.kioskStandby;
    if (!cfg) { return { ...DEFAULTS }; }
    return {
      enabled: !!cfg.enabled,
      idleTimeoutSeconds: cfg.idleTimeoutSeconds > 0 ? cfg.idleTimeoutSeconds : DEFAULTS.idleTimeoutSeconds,
      slideDurationSeconds: cfg.slideDurationSeconds > 0 ? cfg.slideDurationSeconds : DEFAULTS.slideDurationSeconds,
    };
  }

  private engage(): void {
    if (!this.running) { return; }
    if (!this.slides.length) { return; } // never show a blank screensaver
    if (!this.activeSubject.value) {
      this.activeSubject.next(true);
    }
  }

  private armTimer(): void {
    this.clearTimer();
    if (!this.running) { return; }
    // Keep the idle countdown out of Angular's zone so it doesn't retrigger
    // change detection every cycle; re-enter only to flip the active flag.
    this.zone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        this.zone.run(() => this.engage());
      }, this.idleTimeoutMs);
    });
  }

  private clearTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private addListeners(): void {
    if (typeof document === 'undefined') { return; }
    ACTIVITY_EVENTS.forEach(ev =>
      document.addEventListener(ev, this.boundActivity, { passive: true, capture: true }));
  }

  private removeListeners(): void {
    if (typeof document === 'undefined') { return; }
    ACTIVITY_EVENTS.forEach(ev =>
      document.removeEventListener(ev, this.boundActivity, { capture: true } as any));
  }

  /**
   * Build the slide list: fan favourites first, then any items with images,
   * then category images. First non-empty source wins.
   */
  private async loadSlides(): Promise<void> {
    let imgs = await this.safeImages(() => this.cloverApi.getMostSoldItems(), r => r && r.lineItems);
    if (imgs.length < 1) {
      imgs = await this.safeImages(() => this.cloverApi.getAllItems(null, null), r => r);
    }
    if (imgs.length < 1) {
      imgs = await this.safeImages(() => this.cloverApi.getAllCategories(), r => r);
    }
    this.slides = imgs;
  }

  private async safeImages(source: () => any, pick: (res: any) => any[]): Promise<StandbySlide[]> {
    try {
      const obs = source();
      if (!obs || typeof obs.pipe !== 'function') { return []; }
      const res = await obs.pipe(take(1)).toPromise();
      return this.extractSlides(pick(res));
    } catch (e) {
      return [];
    }
  }

  /** Build slides off a list of items/categories, de-duplicated by image. */
  private extractSlides(list: any): StandbySlide[] {
    if (!Array.isArray(list)) { return []; }
    const seen = new Set<string>();
    const out: StandbySlide[] = [];
    for (const entry of list) {
      const src = entry && entry.imageData;
      if (!src || typeof src !== 'string') { continue; }
      if (src.includes('noImageAvailable') || src.includes('default-logo')) { continue; }
      if (seen.has(src)) { continue; }
      seen.add(src);
      const title = (entry.name || '').toString().trim();
      const subtitle = (entry.alternateName || '').toString().trim();
      out.push({
        image: src,
        title: title || undefined,
        subtitle: subtitle && subtitle !== title ? subtitle : undefined,
      });
    }
    return out;
  }
}
